const DEFAULT_RETRY_ATTEMPTS = 3;
const RETRY_DELAY_MS = 2000;

/**
 * Get the configured number of retry attempts from env, with fallback.
 */
export const getMaxRetries = (): number => {
  const envValue = process.env.NEXT_PUBLIC_LMA_API_RETRY_ATTEMPTS;
  if (envValue) {
    const parsed = parseInt(envValue, 10);
    if (!isNaN(parsed) && parsed >= 0) {
      return parsed;
    }
  }
  return DEFAULT_RETRY_ATTEMPTS;
};

export const getDelayTime = (): number => {
  const envValue = process.env.NEXT_PUBLIC_LMA_API_RETRY_DELAY;
  if (envValue) {
    const parsed = parseInt(envValue, 10);
    if (!isNaN(parsed) && parsed >= 0) {
      return parsed;
    }
  }
  return RETRY_DELAY_MS;
};

/**
 * Check if the error is a 504 Gateway Timeout that should be retried.
 */
const isRetryableError = (error: any): boolean => {
  return error?.response?.status === 504;
};

/**
 * Delay helper with incremental backoff.
 */
const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Execute an async function with retry logic for 504 errors.
 * Retries up to NEXT_PUBLIC_LMA_API_RETRY_ATTEMPTS times (default 3).
 */
export const withRetry = async <T>(fn: () => Promise<T>): Promise<T> => {
  const maxRetries = getMaxRetries();
  let lastError: any;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      lastError = error;

      if (isRetryableError(error) && attempt < maxRetries) {
        console.warn(`504 Gateway Timeout - Retry attempt ${attempt + 1}/${maxRetries}`);
        await delay(getDelayTime() * (attempt + 1));
        continue;
      }

      throw error;
    }
  }

  throw lastError;
};
